import * as React from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { isTypingTarget } from './grid-logic';

/**
 * SidePanel — detail panel beside (or over) the main column (T-0140).
 *
 * Mirrors `.rq-panel` in `docs/requirement-library/prototype/
 * selection.css`: a header with a mono identifier, a muted path and a
 * close button; a scrolling body; an optional footer. Place it in
 * `WorkspaceLayout`'s `panel` slot, which decides docked vs floating.
 *
 * It is a non-modal region (`role="complementary"`), not a dialog: the
 * grid behind it stays usable. Esc closes it unless something else
 * already handled the key (an inline edit, an open dialog or palette),
 * or focus is in a text field outside the panel.
 *
 * Building blocks for the body live here too: `PanelSection`,
 * `CitationGrid`, `SegmentedControl`.
 */

export interface SidePanelProps {
  /** Short identifier shown in mono, for example a requirement number. */
  identifier?: React.ReactNode;
  /** Muted breadcrumb-style path beside the identifier. */
  path?: React.ReactNode;
  onClose: () => void;
  /** Accessible name for the region. */
  'aria-label': string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  /** Extra header content between the path and the close button. */
  headerActions?: React.ReactNode;
  className?: string;
}

export function SidePanel({
  identifier,
  path,
  onClose,
  'aria-label': ariaLabel,
  children,
  footer,
  headerActions,
  className,
}: SidePanelProps) {
  const ref = React.useRef<HTMLElement>(null);
  const onCloseRef = React.useRef(onClose);
  onCloseRef.current = onClose;

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      if (document.querySelector('[role="dialog"][data-state="open"]')) return;
      const target = e.target as Node | null;
      const inside = target != null && ref.current?.contains(target);
      // Esc in a text field elsewhere on the page (toolbar search) is
      // that field's key, not ours.
      if (!inside && isTypingTarget(target)) return;
      e.preventDefault();
      onCloseRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  return (
    <aside
      ref={ref}
      role="complementary"
      aria-label={ariaLabel}
      className={cn('flex min-h-0 flex-1 flex-col bg-paper-0', className)}
    >
      <div className="flex flex-none items-center gap-2.5 border-b border-ink-100 px-4 py-3">
        {identifier ? (
          <span className="font-mono text-body-sm font-medium text-ink-900">{identifier}</span>
        ) : null}
        <span className="min-w-0 flex-1 truncate text-meta text-ink-500">{path}</span>
        {headerActions}
        <button
          type="button"
          aria-label="Close panel"
          onClick={onClose}
          className="inline-flex h-[26px] w-[26px] flex-none items-center justify-center rounded-chip text-ink-500 hover:bg-ink-100 hover:text-ink-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
      <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-y-auto p-4">{children}</div>
      {footer ? (
        <div className="flex flex-none items-center justify-between gap-2 border-t border-ink-100 px-4 py-3">
          {footer}
        </div>
      ) : null}
    </aside>
  );
}

SidePanel.displayName = 'SidePanel';

export interface PanelSectionProps {
  label: string;
  children: React.ReactNode;
  className?: string;
}

/** Labelled section: small uppercase label over its content. */
export function PanelSection({ label, children, className }: PanelSectionProps) {
  const id = React.useId();
  return (
    <section aria-labelledby={id} className={className}>
      <h3 id={id} className="mb-1.5 text-meta font-medium uppercase tracking-wide text-ink-500">
        {label}
      </h3>
      {children}
    </section>
  );
}

export interface CitationGridItem {
  label: string;
  value: React.ReactNode;
  /** Render the value in the mono face (ids, versions, file names). */
  mono?: boolean;
}

export interface CitationGridProps {
  items: ReadonlyArray<CitationGridItem>;
  /** `ai` uses the AI surface tokens for AI-derived provenance. */
  tone?: 'default' | 'ai';
  className?: string;
}

/** Key / value grid stating where a record came from (`.cite-grid`). */
export function CitationGrid({ items, tone = 'default', className }: CitationGridProps) {
  return (
    <dl
      className={cn(
        'grid grid-cols-[112px_1fr] gap-x-3 gap-y-1.5 rounded-card border px-3 py-2.5 text-body-sm',
        tone === 'ai' ? 'border-ai-surface-border bg-ai-surface' : 'border-ink-100 bg-paper-50',
        className,
      )}
    >
      {items.map((item) => (
        <React.Fragment key={item.label}>
          <dt className="text-meta text-ink-500">{item.label}</dt>
          <dd
            className={cn('min-w-0 break-words text-ink-900', item.mono && 'font-mono text-meta')}
          >
            {item.value}
          </dd>
        </React.Fragment>
      ))}
    </dl>
  );
}

export interface SegmentedOption<V extends string> {
  value: V;
  label: React.ReactNode;
}

export interface SegmentedControlProps<V extends string> {
  options: ReadonlyArray<SegmentedOption<V>>;
  value: V | null;
  onChange?: (value: V) => void;
  /** Show the current value without allowing a change. */
  readOnly?: boolean;
  /** `sm` is the 26px `.seg.sm`, for example a pain point's severity (T-0188). */
  size?: 'sm' | 'md';
  'aria-label': string;
  className?: string;
}

/**
 * Segmented control (`.seg`): a row of joined buttons, one selected.
 * `role="radiogroup"`; Left / Right / Up / Down move the selection as
 * native radios do, and only the selected segment is a tab stop.
 */
export function SegmentedControl<V extends string>({
  options,
  value,
  onChange,
  readOnly = false,
  size = 'md',
  'aria-label': ariaLabel,
  className,
}: SegmentedControlProps<V>) {
  const refs = React.useRef<Array<HTMLButtonElement | null>>([]);
  const selectedIndex = options.findIndex((o) => o.value === value);
  const tabStop = selectedIndex >= 0 ? selectedIndex : 0;

  const move = (from: number, delta: number) => {
    const next = (from + delta + options.length) % options.length;
    const opt = options[next];
    if (!opt) return;
    refs.current[next]?.focus();
    if (!readOnly) onChange?.(opt.value);
  };

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      aria-readonly={readOnly || undefined}
      className={cn(
        'inline-flex overflow-hidden rounded-card border border-ink-200 focus-within:ring-2 focus-within:ring-accent-amber-600',
        className,
      )}
    >
      {options.map((opt, i) => {
        const on = opt.value === value;
        return (
          <button
            key={opt.value}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="button"
            role="radio"
            aria-checked={on}
            aria-disabled={readOnly || undefined}
            tabIndex={i === tabStop ? 0 : -1}
            onClick={() => {
              if (!readOnly) onChange?.(opt.value);
            }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
                e.preventDefault();
                move(i, 1);
              } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
                e.preventDefault();
                move(i, -1);
              }
            }}
            className={cn(
              'border-r border-ink-200 text-meta font-medium last:border-r-0 focus-visible:outline-none',
              size === 'sm' ? 'h-[26px] px-2' : 'h-[30px] px-2.5',
              on ? 'bg-ink-900 text-paper-0' : 'bg-paper-0',
              !on && (readOnly ? 'text-ink-300' : 'text-ink-700 hover:bg-paper-50'),
              readOnly && 'cursor-default',
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
